import 'arrive';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";

gsap.registerPlugin(ScrollTrigger, TextPlugin);

// Helper to get child elements safely
const q = (root, sel) => (root ? root.querySelector(sel) : null);

// Wrap only non-space characters in spans for safe per-letter stagger (keeps spaces as text nodes)
function wrapCharsSafe(el) {
  if (!el) return [];
  const text = el.textContent || '';
  const frag = document.createDocumentFragment();
  const chars = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === ' ') {
      frag.appendChild(document.createTextNode(' '));
      continue;
    }
    const span = document.createElement('span');
    span.className = 'char';
    span.textContent = ch;
    frag.appendChild(span);
    chars.push(span);
  }
  el.textContent = '';
  el.appendChild(frag);
  return chars;
}

// When timeline block arrives on page (also handles existing on load)
document.arrive('.block-timeline', { existing: true }, function (block) {
  const wrappers = block.querySelectorAll('.timeline-wrapper');
  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    const yearEl = q(wrapper, '.year');
    const textEl = q(wrapper, '.text');
    if (!yearEl && !textEl) return;

    // Create per-letter nodes ONLY for year; text stays as whole element
    const yearChars = yearEl ? wrapCharsSafe(yearEl) : [];

    // Initial masked states bottom -> top
    if (yearChars.length) {
      gsap.set(yearChars, {
        clipPath: 'inset(100% 0 0 0)', // hidden from bottom
        yPercent: 30,
        opacity: 0,
        display: 'inline', // preserve baseline/kerning
        willChange: 'clip-path, transform, opacity',
      });
    }
    if (textEl) {
      gsap.set(textEl, {
        clipPath: 'inset(100% 0 0 0)', // hidden from bottom
        opacity: 0,
        willChange: 'clip-path, opacity',
      });
    }

    // Optional content animations toggles
    const useTextScramble = wrapper.hasAttribute('data-text-scramble');
    const useType = wrapper.hasAttribute('data-text-type');

    // Build GSAP timeline with ScrollTrigger per wrapper
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        start: 'top center',
        toggleActions: 'play none none none',
        once: true,
      },
    });

    // YEAR per-letter reveal bottom -> top
    if (yearChars.length) {
      tl.to(yearChars, {
        clipPath: 'inset(0% 0 0 0)',
        yPercent: 0,
        opacity: 1,
        duration: 0.55,
        ease: 'power2.out',
        stagger: { each: 0.03, from: 'start' },
      }, 0);
    }

    // TEXT reveal as a whole element, slightly after year
    if (textEl) {
      tl.to(textEl, {
        clipPath: 'inset(0% 0 0 0)',
        opacity: 1,
        duration: 0.65,
        ease: 'power2.out',
      }, 0.15);
    }

    // Optional: typing/scramble after reveal (kept, but avoid altering DOM before reveal)
    if (useType && (yearEl || textEl)) {
      if (yearEl) {
        const original = yearChars.length ? (yearChars.map(n => n.textContent).join('')) : (yearEl.textContent || '');
        tl.add(() => { yearEl.textContent = ''; });
        tl.to(yearEl, {
          text: { value: original, speed: 1 },
          duration: Math.min(1.1, Math.max(0.5, original.length * 0.05)),
          ease: 'none',
        }, '>-0.2');
      }
      if (textEl) {
        const original = textChars.length ? (textChars.map(n => n.textContent).join('')) : (textEl.textContent || '');
        tl.add(() => { textEl.textContent = ''; }, '>-0.1');
        tl.to(textEl, {
          text: { value: original, speed: 1 },
          duration: Math.min(1.6, Math.max(0.6, original.length * 0.02)),
          ease: 'none',
        }, '>-0.05');
      }
    } else if (useTextScramble && (yearEl || textEl)) {
      if (yearEl) {
        const original = yearChars.length ? (yearChars.map(n => n.textContent).join('')) : (yearEl.textContent || '');
        tl.add(() => { yearEl.textContent = ''; });
        tl.to(yearEl, {
          text: { value: original, scrambleText: { chars: '0123456789' } },
          duration: 0.8,
        }, '>-0.2');
      }
      if (textEl) {
        const original = textChars.length ? (textChars.map(n => n.textContent).join('')) : (textEl.textContent || '');
        tl.add(() => { textEl.textContent = ''; }, '>-0.1');
        tl.to(textEl, {
          text: { value: original, scrambleText: { chars: 'abcdefghijklmnopqrstuvwxyz' } },
          duration: 1.0,
        }, '>-0.05');
      }
    }
  });
});
